import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mastro_token";

// Access token lives in sessionStorage ONLY (cleared on tab close). Persistence
// across restarts comes exclusively from the httpOnly `mastro_refresh` cookie
// via /auth/refresh — no token/credential is ever kept in localStorage.
export const getToken = () => sessionStorage.getItem(TOKEN_KEY);
export const setToken = (t) => sessionStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => sessionStorage.removeItem(TOKEN_KEY);

// withCredentials so the httpOnly refresh cookie is sent on /auth/refresh
// and /auth/logout (same-origin; explicit for safety).
const api = axios.create({ baseURL: API, withCredentials: true });

export const validateClaim = (claimToken) =>
  api.post("/onboarding/claim/validate", { claim_token: claimToken }).then((r) => r.data);

export const login = (whatsappNumber, password, remember = false) =>
  api
    .post("/auth/login", { whatsapp_number: whatsappNumber, password, remember })
    .then((r) => r.data);

export const registerAccount = (payload) =>
  api
    .post("/onboarding/register", {
      ...payload,
      // MS-L4C — explicit Terms of Service acceptance recorded server-side.
      terms_version: "1.0",
      terms_accepted: true,
    })
    .then((r) => r.data);

// Silently restore a persistent session from the httpOnly refresh cookie.
// Returns the new access token on success; throws on 401 (no valid session).
export const refreshSession = () =>
  api.post("/auth/refresh", {}).then((r) => {
    if (r.data?.access_token) setToken(r.data.access_token);
    return r.data;
  });

// Revoke the persistent session server-side, then drop the local access token.
export const logout = async () => {
  try {
    await api.post("/auth/logout", {});
  } catch (_) {
    // best-effort: always clear the local token regardless of network outcome
  }
  clearToken();
};

// MS-L6B — Pre-Beta account deletion request. Authenticated call: identity is
// resolved by the backend from the Bearer token; the server transitions the
// workspace to DELETION_REQUESTED, revokes every persistent session and clears
// the refresh cookie. The frontend then drops the local access token.
export const requestAccountDeletion = (token) =>
  api
    .post("/account/deletion-request", {}, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((r) => r.data);

export const fetchMe = (token) =>
  api
    .get("/auth/me", { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.data);

export const fetchHome = (token) =>
  api
    .get("/dashboard/home", { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.data);

export const fetchJobs = (token, includeDismissed = false) =>
  api
    .get("/dashboard/jobs", {
      headers: { Authorization: `Bearer ${token}` },
      params: includeDismissed ? { include_dismissed: true } : {},
    })
    .then((r) => r.data);

export const fetchJob = (token, jobId) =>
  api
    .get(`/dashboard/jobs/${jobId}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.data);

export const fetchMediaObjectUrl = (token, mediaId) =>
  api
    .get(`/dashboard/media/${mediaId}`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    })
    .then((r) => URL.createObjectURL(r.data));

export const correctJob = (token, jobId, field, value) =>
  api
    .patch(
      `/dashboard/jobs/${jobId}`,
      { field, value },
      { headers: { Authorization: `Bearer ${token}` } }
    )
    .then((r) => r.data);

export const editJobItem = (token, jobId, itemId, field, value) =>
  api
    .patch(
      `/dashboard/jobs/${jobId}/items/${itemId}`,
      { field, value },
      { headers: { Authorization: `Bearer ${token}` } }
    )
    .then((r) => r.data);

export const completeJob = (token, jobId) =>
  api.post(`/dashboard/jobs/${jobId}/complete`, {}, {
    headers: { Authorization: `Bearer ${token}` },
  }).then((r) => r.data);

export const archiveJob = (token, jobId) =>
  api.post(`/dashboard/jobs/${jobId}/archive`, {}, {
    headers: { Authorization: `Bearer ${token}` },
  }).then((r) => r.data);

export const dismissJob = (token, jobId) =>
  api.post(`/dashboard/jobs/${jobId}/dismiss`, {}, {
    headers: { Authorization: `Bearer ${token}` },
  }).then((r) => r.data);

export const restoreJob = (token, jobId) =>
  api.post(`/dashboard/jobs/${jobId}/restore`, {}, {
    headers: { Authorization: `Bearer ${token}` },
  }).then((r) => r.data);



export const fetchQuotes = (token, includeDismissed = false) =>
  api.get("/dashboard/quotes", {
    headers: { Authorization: `Bearer ${token}` },
    params: includeDismissed ? { include_dismissed: true } : {},
  }).then((r) => r.data);

export const fetchQuote = (token, quoteId) =>
  api.get(`/dashboard/quotes/${quoteId}`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data);

export const confirmQuote = (token, quoteId) =>
  api.post(`/dashboard/quotes/${quoteId}/confirm`, {}, {
    headers: { Authorization: `Bearer ${token}` },
  }).then((r) => r.data);

export const cancelQuote = (token, quoteId) =>
  api.post(`/dashboard/quotes/${quoteId}/cancel`, {}, {
    headers: { Authorization: `Bearer ${token}` },
  }).then((r) => r.data);

export const dismissQuote = (token, quoteId) =>
  api.post(`/dashboard/quotes/${quoteId}/dismiss`, {}, {
    headers: { Authorization: `Bearer ${token}` },
  }).then((r) => r.data);

export const restoreQuote = (token, quoteId) =>
  api.post(`/dashboard/quotes/${quoteId}/restore`, {}, {
    headers: { Authorization: `Bearer ${token}` },
  }).then((r) => r.data);


export const previewVatOverride = (token, quoteId, body) =>
  api.post(`/dashboard/quotes/${quoteId}/vat-override/preview`, body, {
    headers: { Authorization: `Bearer ${token}` },
  }).then((r) => r.data);

export const confirmVatOverride = (token, quoteId, body) =>
  api.post(`/dashboard/quotes/${quoteId}/vat-override/confirm`, body, {
    headers: { Authorization: `Bearer ${token}` },
  }).then((r) => r.data);

export const fetchSettings = (token) =>
  api.get("/dashboard/settings", { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data);
export const saveProfile = (token, body) =>
  api.put("/dashboard/settings/profile", body, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data);
export const saveFiscal = (token, body) =>
  api.put("/dashboard/settings/fiscal", body, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data);
export const saveLogo = (token, logo_data_url) =>
  api.put("/dashboard/settings/logo", { logo_data_url }, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data);
export const changeEmail = (token, recovery_email) =>
  api.put("/dashboard/settings/account/email", { recovery_email }, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data);
export const changePassword = (token, current_password, new_password) =>
  api.put("/dashboard/settings/account/password", { current_password, new_password }, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data);

export async function openQuotePdf(token, quoteId, version, download) {
  const params = [];
  if (version) params.push(`version=${version}`);
  if (download) params.push("download=true");
  const qs = params.length ? `?${params.join("&")}` : "";
  const resp = await api.get(`/dashboard/quotes/${quoteId}/pdf${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
    responseType: "blob",
  });
  const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
  if (download) {
    const a = document.createElement("a");
    a.href = url;
    a.download = `preventivo_v${version || ""}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } else {
    window.open(url, "_blank");
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// Presentation-layer only: format an amount (Decimal string/number) as it-IT €.
// Does NOT alter persisted Decimals or any calculation.
export function formatEuro(value) {
  if (value == null || value === "") return "—";
  const n = typeof value === "number" ? value : Number(String(value).replace(",", "."));
  if (Number.isNaN(n)) return String(value);
  return new Intl.NumberFormat("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n) + " €";
}

// FastAPI 422 returns an array of {msg} objects; normalize to a string.
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const CLAIM_REASON_MESSAGES = {
  invalid: "Questo link di registrazione non è valido.",
  used: "Questo link è già stato utilizzato. Se hai già un account, accedi da WhatsApp.",
  expired:
    'Il link è scaduto. Scrivi "registrati" su WhatsApp per riceverne uno nuovo.',
  already_registered: "Questo account è già attivo. Puoi accedere dalla dashboard.",
};
